// Enums
export enum ContentType {
    ARTICLE = 'ARTICLE',
    VIDEO = 'VIDEO',
    PODCAST = 'PODCAST',
    IMAGE = 'IMAGE',
    DOCUMENT = 'DOCUMENT',
    PLAYLIST = 'PLAYLIST',
    CATEGORY = 'CATEGORY',
    SUBCATEGORY = 'SUBCATEGORY'
}

export enum ContentStatus {
    DRAFT = 'DRAFT',
    PUBLISHED = 'PUBLISHED',
    SCHEDULED = 'SCHEDULED',
    UNPUBLISHED = 'UNPUBLISHED',
    UNCATEGORIZED = 'UNCATEGORIZED'
}

export enum ContentSortField {
    CREATED_AT = 'CREATED_AT',
    UPDATED_AT = 'UPDATED_AT',
    TITLE = 'TITLE',
    VIEWS = 'VIEWS',
    DOWNLOADS = 'DOWNLOADS',
    HELPFUL = 'HELPFUL',
    NAME = 'NAME'
}

export enum SortDirection {
    ASC = 'ASC',
    DESC = 'DESC'
}

export enum TimePeriod {
    TODAY = 'TODAY',
    YESTERDAY = 'YESTERDAY',
    LAST_WEEK = 'LAST_WEEK',
    LAST_MONTH = 'LAST_MONTH',
    LAST_90_DAYS = 'LAST_90_DAYS',
    LAST_YEAR = 'LAST_YEAR',
    ALL_TIME = 'ALL_TIME',
    CUSTOM = 'CUSTOM'
}

// Input types
export interface TimeRangeInput {
    period?: TimePeriod;
    startDate: number;
    endDate: number;
}

export interface ContentSortInput {
    field: ContentSortField;
    direction: SortDirection;
}

export interface ContentFilterInput {
    contentTypes?: ContentType[] | undefined;
    types?: ContentType[] | undefined;
    categoryIds?: string[] | undefined;
    status?: ContentStatus | undefined;
    tags?: string[] | undefined;
    createdBy?: string | undefined;
    timeRange?: TimeRangeInput | undefined;
    parentCategoryId?: string | undefined;
}

export interface ComplexFilterInput extends ContentFilterInput {
    // Additional filter parameters can be added here if needed
}

export interface Pagination {
    total: number;
    page: number;
    limit: number;
    pages: number;
}

// Base content interfaces
export interface BaseContent {
    _id: string;
    createdAt: string;
    updatedAt?: string;
    categoryIds?: string[];
}

export interface EngagementMetrics {
    views: number;
    downloads: number;
    helpful: number;
    notHelpful: number;
}

// Content type interfaces
export interface Article extends BaseContent {
    title: string;
    content?: string;
    status?: ContentStatus;
    engagement?: EngagementMetrics;
}

export interface VideoFile {
    title: string;
    url?: string;
    status?: ContentStatus;
}

export interface Video extends BaseContent {
    videos: VideoFile[];
    engagement?: EngagementMetrics;
}

export interface Podcast extends BaseContent {
    title: string;
    description?: string;
    status?: ContentStatus;
    engagement?: EngagementMetrics;
}

export interface ImageFile {
    title: string;
    url?: string;
    categoryIds?: string[];
    status?: string;
    description?: string;
}

export interface Image extends BaseContent {
    files: ImageFile[];
    engagement?: EngagementMetrics;
    createdBy?: {
        name: string;
        id: string;
    };
}

export interface DocumentFile {
    title: string;
    url?: string;
    categoryIds?: string[];
    status?: string;
    description?: string;
}

export interface Document extends BaseContent {
    files: DocumentFile[];
    engagement?: EngagementMetrics;
}

export interface PlaylistItem {
    title: string;
    contentId?: string;
    contentType?: ContentType;
}

export interface Playlist extends BaseContent {
    name: string;
    description?: string;
    items: PlaylistItem[];
}

export interface Category extends BaseContent {
    name: string;
    description?: string;
    parentId?: string;
}

// Union type for all content types
export type UnifiedContentItem = Article | Video | Podcast | Image | Document | Playlist | Category;

// Response types
export interface ContentResponse {
    success: boolean;
    message?: string;
    data?: UnifiedContentItem[];
    pagination?: Pagination;
}

export interface ComplexContentFilteringResponse extends ContentResponse {
    // Additional fields specific to complex filtering if needed
}

// Statistics types
export interface ContentStatsData {
    categorized: { count: number; previousCount: number };
    uncategorized: { count: number; previousCount: number };
    draft: { count: number; previousCount: number };
    published: { count: number; previousCount: number };
    scheduled: { count: number; previousCount: number };
    unpublished: { count: number; previousCount: number };
    total: number;
}

export interface ContentStatsResponse {
    success: boolean;
    message: string;
    data?: ContentStatsData;
}

// Distribution types
export interface DistributionItem {
    type: ContentType;
    count: number;
    percentage: number;
}

export interface EngagementStats {
    articleReadTime: number;
    videoViewTime: number;
    podcastListenTime: number;
    documentReadTime: number;
}

export interface PublishingStatus {
    published: number;
    unpublished: number;
    publishedPercentage: number;
    unpublishedPercentage: number;
}

export interface ContentTypeDistributionData {
    distribution: DistributionItem[];
    engagement: EngagementStats;
    publishingStatus: PublishingStatus;
}

export interface ContentTypeDistributionResponse {
    success: boolean;
    message: string;
    data?: ContentTypeDistributionData;
}

// Trends types
export interface PublishingTrendsItem {
    date: string;
    totalPublished: number;
    articlesPublished: number;
    videosPublished: number;
    podcastsPublished: number;
    documentsPublished: number;
    imagesPublished: number;
}

export interface ContentPublishingTrendsResponse {
    success: boolean;
    message: string;
    data?: PublishingTrendsItem[];
}
